import React, { useState, useEffect } from 'react';
import { dotLogsAPI } from '../../services/api';
import { useAuthStore } from '../../store';
import { toast } from 'react-toastify';
import './DOTLogs.css';
import DateInput from '../../components/DateInput/DateInput';
import { toInputDate } from '../../utils/dateFormat';
import { downloadXlsx } from '../../utils/excel';

const DOT_SHEETS = [['sessions', 'Sessions'], ['browsing', 'Browsing'], ['router-system', 'Router']];

const today = () => toInputDate();

const LOG_PATH = '/var/log/dot-compliance';

const DOTLogs = () => {
    const { admin } = useAuthStore();
    const [tab, setTab]             = useState('sessions');
    const [startDate, setStartDate] = useState(today());
    const [endDate, setEndDate]     = useState(today());
    const [nasIp, setNasIp]         = useState('');
    const [nasList, setNasList]     = useState([]);
    const [rows, setRows]           = useState([]);
    const [summary, setSummary]     = useState('');
    const [loading, setLoading]     = useState(false);
    const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
    const [syslogStatus, setSyslogStatus] = useState(null);
    const [zipDate, setZipDate]     = useState(today());

    const isSuperadmin = admin?.role === 'superadmin';

    useEffect(() => {
        if (!isSuperadmin) return;
        dotLogsAPI.getSyslogStatus().then(res => setSyslogStatus(res.data.data)).catch(() => {});
    }, [isSuperadmin]);

    // Refresh NAS list when start date changes
    useEffect(() => {
        if (!isSuperadmin || !startDate) return;
        fetchNasList();
    }, [startDate, isSuperadmin]);

    useEffect(() => {
        if (!isSuperadmin || !startDate) return;
        fetchData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, nasIp, tab, isSuperadmin]);

    if (!isSuperadmin) {
        return (
            <div className="dot-access-denied">
                <i className="fas fa-lock"></i>
                <h2>Superadmin Access Only</h2>
                <p>DoT compliance logs are restricted to superadmin.</p>
            </div>
        );
    }

    async function fetchNasList() {
        try {
            const res = await dotLogsAPI.listNas(startDate);
            setNasList(res.data.data || []);
        } catch {
            setNasList([]);
        }
    }

    async function fetchData(page = 1) {
        setLoading(true);
        setRows([]);
        try {
            if (tab === 'summary') {
                const res = await dotLogsAPI.getSummary(startDate);
                setSummary(res.data.data || 'No report available for this date.');
            } else {
                const params = { startDate, endDate, page, limit: 100 };
                if (nasIp) params.nasIp = nasIp;
                const fetchers = {
                    sessions:       dotLogsAPI.getSessions,
                    browsing:       dotLogsAPI.getBrowsing,
                    'router-system': dotLogsAPI.getRouterSystem,
                };
                const res = await fetchers[tab](params);
                setRows(res.data.data || []);
                setPagination(res.data.pagination || { page: 1, total: 0, pages: 1 });
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to fetch logs');
        } finally {
            setLoading(false);
        }
    }

    function handleDownloadCsv(type) {
        const params = { date: startDate, type };
        if (nasIp) params.nasIp = nasIp;
        const url = dotLogsAPI.downloadUrl(params);
        const token = useAuthStore.getState().token;
        setLoading(true);
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => { if (!r.ok) throw new Error('Download failed'); return r.blob(); })
            .then(blob => {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `dot-${type}-${startDate}${nasIp ? `-${nasIp}` : ''}.csv`;
                a.click();
                URL.revokeObjectURL(a.href);
            })
            .catch(() => toast.error('Download failed'))
            .finally(() => setLoading(false));
    }

    // The day's three logs — sessions, browsing, router — as one Excel file, a sheet
    // each (the CSVs stay the DoT's own format).
    async function handleDownloadExcel() {
        setLoading(true);
        try {
            const sheets = await Promise.all(DOT_SHEETS.map(async ([type, name]) => {
                const params = { date: startDate, type };
                if (nasIp) params.nasIp = nasIp;
                const { data } = await dotLogsAPI.downloadTable(params);
                return { name, columns: (data.header || []).map((label, i) => ({ label, value: (r) => r[i] })), rows: data.rows || [] };
            }));
            downloadXlsx(`dot-logs-${startDate}${nasIp ? `-${nasIp}` : ''}`, { sheets });
        } catch {
            toast.error('Download failed');
        } finally {
            setLoading(false);
        }
    }

    function handleDownloadZip() {
        if (!zipDate) return toast.error('Select a date for ZIP download');
        const url = dotLogsAPI.downloadZipUrl(zipDate);
        const token = useAuthStore.getState().token;
        setLoading(true);
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => {
                if (!r.ok) return r.json().then(j => { throw new Error(j.message || 'Not found'); });
                return r.blob();
            })
            .then(blob => {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `dot-compliance-${zipDate}.zip`;
                a.click();
                URL.revokeObjectURL(a.href);
            })
            .catch(err => toast.error(err.message || 'ZIP download failed'))
            .finally(() => setLoading(false));
    }

    const sessionCols     = ['timestamp','mac_address','ip_address','username','nas_ip','session_id','event','start_time','stop_time','duration_sec','upload_bytes','download_bytes','terminate_cause'];
    const browsingCols    = ['timestamp','mac_address','ip_address','username','nas_ip','protocol','domain','url','bytes'];
    const routerSysCols   = ['timestamp','nas_ip','nas_identifier','router_time','topics','message'];
    const cols = tab === 'sessions' ? sessionCols : tab === 'browsing' ? browsingCols : routerSysCols;

    function fmtBytes(b) {
        const n = parseInt(b) || 0;
        if (n < 1024)        return `${n} B`;
        if (n < 1024*1024)   return `${(n/1024).toFixed(1)} KB`;
        return `${(n/1024/1024).toFixed(2)} MB`;
    }

    const dateRangeLabel = startDate === endDate
        ? startDate
        : `${startDate} → ${endDate}`;

    return (
        <div className="dot-page">
            {/* Header */}
            <div className="dot-header">
                <div className="dot-title">
                    <div className="dot-icon"><i className="fas fa-shield-alt"></i></div>
                    <div>
                        <h1>DoT Compliance Logs</h1>
                        <p>Department of Telecommunications — User Activity Records</p>
                        <span className="dot-storage-info">
                            <i className="fas fa-hdd"></i>
                            Storage: <code>{LOG_PATH}/YYYY/MM/DD/NAS-&#123;ip&#125;/</code>
                            &nbsp;· Archives as <code>YYYY-MM-DD.zip</code> daily at 00:30
                        </span>
                        {syslogStatus && (
                            <span className={`dot-syslog-badge ${syslogStatus.enabled ? 'on' : 'off'}`}>
                                <i className="fas fa-broadcast-tower"></i>
                                {syslogStatus.enabled
                                    ? `Syslog active → ${syslogStatus.host}:${syslogStatus.port}`
                                    : 'Syslog not configured (logs stored locally only)'}
                            </span>
                        )}
                    </div>
                </div>

                {/* ZIP download panel */}
                <div className="dot-zip-panel">
                    <label><i className="fas fa-file-archive"></i> Download Archive ZIP</label>
                    <div className="dot-zip-row">
                        <DateInput type="date" value={zipDate} max={today()}
                            onChange={e => setZipDate(e.target.value)} />
                        <button className="dot-zip-btn" onClick={handleDownloadZip} disabled={loading}>
                            <i className="fas fa-download"></i> Download ZIP
                        </button>
                    </div>
                    <span className="dot-zip-hint">Archived ZIPs available for past days</span>
                </div>
            </div>

            {/* Filters */}
            <div className="dot-filters">
                <div className="dot-filter-group">
                    <label><i className="fas fa-calendar-alt"></i> Start Date</label>
                    <DateInput type="date" value={startDate} max={today()}
                        onChange={e => {
                            setStartDate(e.target.value);
                            if (e.target.value > endDate) setEndDate(e.target.value);
                        }} />
                </div>
                <div className="dot-filter-group">
                    <label><i className="fas fa-calendar-check"></i> End Date</label>
                    <DateInput type="date" value={endDate} min={startDate} max={today()}
                        onChange={e => setEndDate(e.target.value)} />
                </div>
                <div className="dot-filter-group">
                    <label><i className="fas fa-server"></i> NAS Device</label>
                    <select value={nasIp} onChange={e => setNasIp(e.target.value)}>
                        <option value="">All NAS Devices</option>
                        {nasList.map(ip => (
                            <option key={ip} value={ip}>{ip}</option>
                        ))}
                    </select>
                </div>
                <button className="dot-refresh-btn" onClick={() => fetchData(1)} disabled={loading}>
                    <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i> Refresh
                </button>
                <div className="dot-csv-actions">
                    <button className="dot-dl-btn" onClick={handleDownloadExcel} disabled={loading} title="Sessions, browsing and router logs of the day — a sheet each">
                        <i className="fas fa-file-excel"></i> Excel
                    </button>
                    <button className="dot-dl-btn secondary" onClick={() => handleDownloadCsv('sessions')} disabled={loading}>
                        <i className="fas fa-file-csv"></i> Sessions CSV
                    </button>
                    <button className="dot-dl-btn secondary" onClick={() => handleDownloadCsv('browsing')} disabled={loading}>
                        <i className="fas fa-file-csv"></i> Browsing CSV
                    </button>
                    <button className="dot-dl-btn secondary" onClick={() => handleDownloadCsv('router-system')} disabled={loading}>
                        <i className="fas fa-file-csv"></i> Router CSV
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="dot-tabs">
                {['sessions', 'browsing', 'router-system', 'summary'].map(t => (
                    <button key={t} className={`dot-tab ${tab === t ? 'active' : ''}`}
                        onClick={() => setTab(t)}>
                        <i className={`fas ${t === 'sessions' ? 'fa-wifi' : t === 'browsing' ? 'fa-globe' : t === 'router-system' ? 'fa-server' : 'fa-chart-bar'}`}></i>
                        {t === 'sessions' ? 'Session Logs' : t === 'browsing' ? 'Browsing Logs' : t === 'router-system' ? 'Router System Log' : 'Daily Summary'}
                        {tab === t && rows.length > 0 && t !== 'summary' &&
                            <span className="dot-count">{pagination.total}</span>}
                    </button>
                ))}
            </div>

            {/* Summary Tab */}
            {tab === 'summary' && (
                <div className="dot-summary-box">
                    {loading
                        ? <div className="dot-loading"><i className="fas fa-spinner fa-spin"></i></div>
                        : <pre>{summary || 'No summary report for this date. Run daily summary job at 23:55.'}</pre>}
                </div>
            )}

            {/* Table */}
            {tab !== 'summary' && (
                <div className="dot-table-wrap">
                    {loading ? (
                        <div className="dot-loading"><i className="fas fa-spinner fa-spin"></i> Loading logs...</div>
                    ) : rows.length === 0 ? (
                        <div className="dot-empty">
                            <i className="fas fa-inbox"></i>
                            <p>No {tab} logs found for <strong>{dateRangeLabel}</strong>{nasIp ? ` on ${nasIp}` : ''}.</p>
                        </div>
                    ) : (
                        <>
                            <div className="dot-table-meta">
                                Showing {rows.length} of {pagination.total} records &nbsp;·&nbsp;
                                <strong>{dateRangeLabel}</strong>
                                {nasIp && <> &nbsp;·&nbsp; NAS: <code>{nasIp}</code></>}
                            </div>
                            <div className="dot-table-scroll">
                                <table className="dot-table">
                                    <thead>
                                        <tr>
                                            <th className="dot-sr">SR. NO.</th>
                                            {cols.map(c => <th key={c}>{c.replace(/_/g, ' ').toUpperCase()}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, i) => (
                                            <tr key={i} className={row.event === 'stop' ? 'row-stop' : row.event === 'start' ? 'row-start' : ''}>
                                                <td className="dot-sr">{((pagination.page || 1) - 1) * (pagination.limit || 100) + i + 1}</td>
                                                {cols.map(c => (
                                                    <td key={c}>
                                                        {(c === 'upload_bytes' || c === 'download_bytes' || c === 'bytes')
                                                            ? fmtBytes(row[c])
                                                            : c === 'event'
                                                                ? <span className={`dot-badge ${row[c]}`}>{row[c]}</span>
                                                                : row[c] || '—'}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {pagination.pages > 1 && (
                                <div className="dot-pagination">
                                    <button disabled={pagination.page <= 1}
                                        onClick={() => fetchData(pagination.page - 1)}>
                                        <i className="fas fa-chevron-left"></i> Prev
                                    </button>
                                    <span>Page {pagination.page} of {pagination.pages}</span>
                                    <button disabled={pagination.page >= pagination.pages}
                                        onClick={() => fetchData(pagination.page + 1)}>
                                        Next <i className="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default DOTLogs;
